/* Real product models; lazy assets, native variants, and independent viewer lifecycles.
   A viewer loads by itself as soon as it is the view on show, on phones as on desktop. The model is fetched a
   little before it scrolls into sight, so it is usually ready on arrival. Visitors who have asked their browser to
   save data, or whose connection is reported as slow, keep the tap-to-load button instead. */
(() => {
  'use strict';
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const fine = matchMedia('(hover: hover) and (pointer: fine)');
  const narrow = matchMedia('(max-width: 749px)'), coarse = matchMedia('(pointer: coarse)');
  const models = new Map(), pending = new Map(), gone = new Set();
  const cameraViews = {
    // The X-Belt opens on its badge side (the badge sits on the left arm).
    belt: {hero:[-.55,.42],front:[0,.12],top:[-.55,1.48],under:[-.55,-.88]},
    static: {hero:[-.72,.30],front:[0,.08],top:[-.72,1.48],under:[-.72,-.88]}
  };
  const heroPose = kind => { const a = cameraViews[kind]?.hero || [-.96,.43]; return {angle:a[0],elevation:a[1],zoom:1}; };
  // X-Badge names as the belt options and the configurator write them, to the badge options inside the belt model.
  const badgeKey = title => {
    const k = String(title || '').toLowerCase().replace(/[^a-z]/g, '');
    return /lebanon|cedar|street/.test(k) ? 'lebanon' : /women/.test(k) ? 'women' : /muscle/.test(k) ? 'muscleup' : /dip/.test(k) ? 'dips' : /pull/.test(k) ? 'pullup' : '';
  };
  let rendererPromise;
  // Loading without being asked is for visitors whose browser does not report data saver, a slow connection or a
  // phone with very little memory. Those keep the tap-to-load button. (Safari and Firefox report none of these.)
  const connection = () => navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  function autoAllowed() {
    const link = connection();
    return !(link && (link.saveData || /2g|3g/.test(link.effectiveType || ''))) && !(navigator.deviceMemory && navigator.deviceMemory < 3);
  }

  function loadRenderer(url) {
    if (window.XEParallettesRenderer) return Promise.resolve(window.XEParallettesRenderer);
    if (!rendererPromise) {
      rendererPromise = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = url; script.async = true;
        const timeout = setTimeout(() => { script.remove(); reject(new Error('3D renderer timed out')); },15000);
        script.onload = () => {
          clearTimeout(timeout);
          if (window.XEParallettesRenderer) resolve(window.XEParallettesRenderer);
          else { script.remove(); reject(new Error('3D renderer is unavailable')); }
        };
        script.onerror = () => { clearTimeout(timeout); script.remove(); reject(new Error('3D renderer could not load')); };
        document.head.append(script);
      }).catch(error => { rendererPromise = null; throw error; });
    }
    return rendererPromise;
  }
  function abortable(promise, signal) {
    return new Promise((resolve,reject) => {
      const abort = () => reject(new DOMException('Cancelled','AbortError'));
      if (signal.aborted) { abort(); return; }
      signal.addEventListener('abort',abort,{once:true});
      promise.then(value => { signal.removeEventListener('abort',abort); resolve(value); },error => { signal.removeEventListener('abort',abort); reject(error); });
    });
  }
  // One download per model, shared by every viewer and by the early fetch; finished models are kept for the page's lifetime.
  // A download that stalls is given up after 25 seconds, so a later attempt (or the original file) starts afresh.
  function modelBytes(url, priority) {
    if (models.has(url)) return Promise.resolve(models.get(url));
    // A file the server says is not there is not asked for twice.
    if (gone.has(url)) return Promise.reject(new Error('3D model could not load'));
    if (!pending.has(url)) {
      const job = (async () => {
        const control = new AbortController(), options = {credentials:'omit',signal:control.signal};
        if (priority) options.priority = priority;
        // A product page that opens in 3D starts this download while the page is still arriving
        // (snippets/xe-product-3d-gallery.liquid); use that request rather than making a second one.
        const early = window.XEModelEarly?.[url];
        if (early) delete window.XEModelEarly[url];
        let timer;
        const stalled = new Promise((_,reject) => { timer = setTimeout(() => { control.abort(); reject(new Error('3D model timed out')); },25000); });
        const download = (async () => {
          const response = (early && await early.catch(() => null)) || await fetch(url, options);
          if (!response.ok) { if (response.status === 404 || response.status === 410) gone.add(url); throw new Error('3D model could not load'); }
          return response.json();
        })();
        let payload;
        try { payload = await Promise.race([download,stalled]); } finally { clearTimeout(timer); }
        if (payload.format !== 'glb' || payload.version !== 1 || typeof payload.data !== 'string' || payload.data.length > 16000000) throw new Error('Invalid 3D payload');
        const raw = atob(payload.data), bytes = new Uint8Array(raw.length);
        for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i);
        if (bytes.byteLength < 28 || new DataView(bytes.buffer).getUint32(0,true) !== 0x46546c67) throw new Error('Invalid 3D model');
        models.set(url,bytes.buffer); return bytes.buffer;
      })();
      pending.set(url,job);
      const done = () => pending.delete(url);
      job.then(done,done);
    }
    return pending.get(url);
  }
  // Phones and tablets take the lighter copy of a model: the same shape and materials, with the wood textures
  // stored as JPEG, a quarter of the download. The original file stays as the fallback and is what larger screens use.
  // A lighter copy that failed once on this page is not tried again.
  const sourcesFor = model => model.lite && !gone.has(model.lite) && (narrow.matches || coarse.matches) ? [model.lite, model.url] : [model.url];
  // Fetch the renderer and a model ahead of need. Failures are left for the real load to report.
  function warm(viewer, evenIfHidden = false) {
    const model = viewer?.config?.models[viewer.key];
    if (!model || !autoAllowed() || !window.WebGLRenderingContext || (!evenIfHidden && viewer.closest('[hidden]'))) return;
    loadRenderer(viewer.dataset.renderer).catch(() => {});
    modelBytes(sourcesFor(model)[0],'low').catch(() => {});
  }
  function listen(target, type, handler, cleanup, options) {
    target.addEventListener(type,handler,options);
    cleanup.push(() => target.removeEventListener(type,handler,options));
  }

  class ProductViewer extends HTMLElement {
    connectedCallback() {
      if (this.alive) return;
      this.alive = true; this.cleanup = []; this.frame = 0; this.lastFrame = 0; this.lastRender = 0;
      this.active = false; this.visible = false; this.ready = false; this.loading = false;
      this.requestToken = 0; this.points = new Map(); this.pinch = 0; this.spin = null; this.velocity = {a:0,e:0,t:0}; this.engagedTimer = 0;
      this.auto = false; this.userPaused = false; this.freeRotation = false; this.everAsked = false; this.autoFailed = false;
      this.pose = {angle:-.96,elevation:.43,zoom:1}; this.dirty = false; this.tween = null;
      this.canvas = this.querySelector('canvas'); this.poster = this.querySelector('[data-xe-3d-poster]');
      this.status = this.querySelector('[data-xe-3d-status]'); this.loadButton = this.querySelector('[data-xe-3d-load]');
      this.spinner = this.querySelector('[data-xe-3d-loading]'); this.controls = this.querySelector('[data-xe-3d-controls]');
      this.autoButton = this.querySelector('[data-xe-3d-auto]'); this.touchButton = this.querySelector('[data-xe-3d-touch]');
      this.angleSelect = this.querySelector('[data-xe-3d-angle]');
      try { this.config = JSON.parse(this.querySelector('[data-xe-3d-config]').textContent); }
      catch (_) { this.alive = false; return; }
      this.key = this.config.models[this.dataset.model] ? this.dataset.model : 'black';
      this.pose = heroPose(this.config.models[this.key].kind);
      this.dataset.state = 'idle'; this.canvas.hidden = true; this.poster.hidden = false;
      this.poster.src = this.config.models[this.key].poster; this.spinner.hidden = true;
      this.offerLoad(); this.controls.hidden = true;
      const bind = (target,type,handler,options) => listen(target,type,handler,this.cleanup,options);
      bind(this.loadButton,'click',() => this.load());
      this.querySelectorAll('[data-xe-3d-view]').forEach(button => bind(button,'click',() => this.view(button.getAttribute('data-xe-3d-view'))));
      if (this.angleSelect) bind(this.angleSelect,'change',() => this.view(this.angleSelect.value));
      this.querySelectorAll('[data-xe-3d-zoom]').forEach(button => bind(button,'click',() => this.zoomBy(button.getAttribute('data-xe-3d-zoom')==='in' ? .85 : 1.15)));
      bind(this.querySelector('[data-xe-3d-reset]'),'click',() => this.view('hero'));
      bind(this.autoButton,'click',() => {
        if (!this.motionAllowed() || !this.ready) return;
        this.auto = !this.auto; this.userPaused = !this.auto; this.tween = null; this.speak(true);
        if (this.auto) this.clearView();
        this.refresh(); this.hint();
      });
      bind(this.touchButton,'click',() => {
        this.freeRotation = !this.freeRotation; this.speak(true);
        this.dataset.freeRotation = String(this.freeRotation);
        this.touchButton.setAttribute('aria-pressed',String(this.freeRotation));
        this.points.clear(); this.pinch = 0; this.hint();
      });
      this.querySelectorAll('[data-xe-3d-badge]').forEach(button => bind(button,'click',() => this.setModel(button.getAttribute('data-xe-3d-badge'))));
      // X-Belt: the badge on the model follows the belt options on a product page, or the preview list where there are no options.
      this.badge = null;
      const badgeSelect = this.querySelector('[data-xe-3d-badge-select]');
      if (badgeSelect) bind(badgeSelect,'change',() => this.setBadge(badgeSelect.value));
      if (this.hasBadges()) {
        const configurator = this.closest('product-info')?.querySelector('x-belt-configurator');
        if (configurator?.state) this.followBelt(configurator.state.style, configurator.state.badges?.[0]);
        bind(document,'xe:xbelt-change',event => {
          const info = this.closest('product-info');
          if (info && info.contains(event.target)) this.followBelt(event.detail?.style, event.detail?.badge);
        });
      }
      bind(this.canvas,'pointerdown',event => this.pointerDown(event));
      bind(this.canvas,'pointermove',event => this.pointerMove(event));
      for (const event of ['pointerup','pointercancel','lostpointercapture']) bind(this.canvas,event,e => this.pointerUp(e));
      bind(this.canvas,'wheel',event => {
        if (!this.ready || !this.canPaint() || !fine.matches || document.activeElement !== this.canvas || event.ctrlKey || event.metaKey) return;
        event.preventDefault(); this.zoomBy(Math.exp(Math.sign(event.deltaY)*.08));
      },{passive:false});
      bind(this.canvas,'keydown',event => this.keyDown(event));
      bind(this.canvas,'webglcontextlost',event => {
        event.preventDefault(); this.cancelLoad(); this.ready = false; this.dataset.state = 'restoring';
        this.canvas.hidden = true; this.poster.hidden = false; this.loadButton.hidden = true;
        this.status.textContent = 'Restoring the 3D view…'; this.refresh();
      });
      bind(this.canvas,'webglcontextrestored',() => {
        this.dataset.state = 'idle'; this.status.textContent = 'Explore every angle.'; this.offerLoad();
        this.refresh();
      });
      // The browser's reading of the connection can change after the page has loaded: follow it.
      const link = connection();
      if (link?.addEventListener) bind(link,'change',() => this.refresh());
      bind(document,'visibilitychange',() => this.refresh());
      bind(document,'xe:overlay',() => this.refresh());
      bind(reduced,'change',() => {
        if (reduced.matches) { this.auto = false; if (this.tween) this.pose = this.tween.to; this.tween = null; }
        this.refresh(); this.hint();
      });
      bind(fine,'change',() => { this.points.clear(); this.freeRotation = false; this.dataset.freeRotation = 'false'; this.touchButton.setAttribute('aria-pressed','false'); this.refresh(); this.hint(); });
      if (window.IntersectionObserver) {
        this.intersection = new IntersectionObserver(entries => { this.visible = entries[0].isIntersecting; this.refresh(); },{threshold:.02});
        this.intersection.observe(this);
        // A screen and a half before the viewer is reached, start fetching what it will show (2.7.0: the shorter home
        // page put its spotlight inside 2.5 screens, so the first screen fetched the model).
        this.approach = new IntersectionObserver(entries => {
          if (!entries.some(entry => entry.isIntersecting)) return;
          this.near = true; if (this.active) warm(this);
        },{rootMargin:'150% 0px 150% 0px'});
        this.approach.observe(this.closest('xe-product-media') || this);
      } else { this.visible = true; this.near = true; }
      if (window.ResizeObserver) {
        this.resize = new ResizeObserver(() => this.requestPaint()); this.resize.observe(this);
      } else bind(window,'resize',() => this.requestPaint(),{passive:true});
      this.updateBadgeButtons(); this.setControls(false);
    }
    disconnectedCallback() { this.destroy(); }
    destroy() {
      if (!this.alive) return;
      this.alive = false; this.cancelLoad(); cancelAnimationFrame(this.frame); this.frame = 0;
      this.intersection?.disconnect(); this.approach?.disconnect(); this.resize?.disconnect(); this.cleanup.forEach(remove => remove());
      this.cleanup = []; this.points.clear(); this.disengage();
    }
    motionAllowed() { return this.dataset.motion === 'true' && document.body.classList.contains('xe-motion') && !reduced.matches; }
    canPaint() { return this.alive && this.active && this.visible && !this.closest('[hidden]') && !document.hidden && document.documentElement.dataset.xeOverlay !== 'true'; }
    setControls(ready) {
      this.controls.hidden = !ready;
      if (this.angleSelect) this.angleSelect.disabled = !ready;
      this.controls.querySelectorAll('[data-xe-3d-view],[data-xe-3d-zoom],[data-xe-3d-reset],[data-xe-3d-auto],[data-xe-3d-touch]').forEach(b => b.disabled = !ready);
      this.autoButton.disabled = !ready || !this.motionAllowed(); this.touchButton.hidden = fine.matches;
      this.autoButton.setAttribute('aria-pressed',String(this.auto));
      this.autoButton.setAttribute('aria-label',this.auto ? 'Pause automatic rotation' : 'Rotate automatically');
    }
    setActive(active, start = false) {
      this.active = Boolean(active);
      if (!this.active) { this.points.clear(); this.pinch = 0; if (this.loading) this.cancelLoad(); }
      else if (this.near && this.wantsAuto()) warm(this);
      this.refresh();
      if (start && this.active && !this.ready && !this.loading) this.load();
    }
    // Load without being asked, unless that already failed here or the visitor's connection says not to.
    wantsAuto() { return this.dataset.autoload !== 'false' && !this.autoFailed && autoAllowed(); }
    // The tap-to-load button is only offered when the viewer will not load by itself.
    offerLoad() { this.loadButton.disabled = false; this.loadButton.hidden = this.wantsAuto(); }
    setModel(key) {
      if (!this.config?.models[key] || key === this.key) return;
      // Same file (the X-Belt with or without its badge): switch the shown part, no second download or reload.
      if (this.config.models[key].url === this.config.models[this.key].url) {
        this.key = key; this.dataset.model = key; this.poster.src = this.config.models[key].poster;
        this.updateBadgeButtons(); this.applyVariant(); return;
      }
      const differentHeight = this.config.models[key].kind !== this.config.models[this.key].kind;
      this.cancelLoad(); this.ready = false; this.auto = false; this.key = key; this.dataset.model = key;
      this.canvas.hidden = true; this.poster.hidden = false; this.poster.src = this.config.models[key].poster;
      this.dataset.state = 'idle'; this.offerLoad();
      this.loadButton.querySelector('span').textContent = 'EXPLORE IN 3D'; this.status.textContent = 'Explore every angle.';
      if (differentHeight) this.pose = heroPose(this.config.models[key].kind);
      this.tween = null; this.updateBadgeButtons(); this.refresh();
      this.clearView();
    }
    updateBadgeButtons() {
      this.querySelectorAll('[data-xe-3d-badge]').forEach(b => b.setAttribute('aria-pressed',String(b.getAttribute('data-xe-3d-badge') === this.key)));
      const select = this.querySelector('[data-xe-3d-badge-select]');
      if (select) select.value = this.config.models[this.key]?.variant === 'badge:classic' ? 'classic' : this.badge || 'lebanon';
    }
    // Models whose config names a badge part ("variant": "badge:<option>").
    hasBadges() { return Object.values(this.config?.models || {}).some(model => /^badge:/.test(model.variant || '')); }
    keyFor(classic) { return Object.keys(this.config.models).find(key => (this.config.models[key].variant === 'badge:classic') === classic && /^badge:/.test(this.config.models[key].variant || '')); }
    // A badge to preview: "classic" shows the belt without one.
    setBadge(value) {
      if (!this.hasBadges()) return;
      const classic = value === 'classic', option = classic ? '' : badgeKey(value) || value;
      if (!classic) this.badge = option;
      const key = this.keyFor(classic);
      if (key && key !== this.key) this.setModel(key); else { this.updateBadgeButtons(); this.applyVariant(); }
    }
    // The belt options on a product page: style "classic" or "badge", and the included badge's name.
    followBelt(style, badge) { this.setBadge(style === 'classic' ? 'classic' : badgeKey(badge) || this.badge || 'lebanon'); }
    // Shows the switchable part named by the model's config, with the chosen badge on the badged belt.
    applyVariant() {
      const model = this.config.models[this.key], [part, option] = String(model?.variant || '').split(':');
      if (!part || !option) return;
      const shown = part === 'badge' && option !== 'classic' && this.badge ? this.badge : option;
      this.dataset.badge = shown;
      if (this.ready && this.renderer?.variant?.(part, shown)) this.requestPaint();
    }
    cancelLoad() {
      this.requestToken++; this.controller?.abort(); this.controller = null;
      clearTimeout(this.loadTimeout); this.loading = false; this.spinner.hidden = true;
      this.renderer?.dispose(); this.renderer = null; this.ready = false;
      this.offerLoad();
      if (this.dataset.state === 'loading') { this.dataset.state = 'idle'; this.status.textContent = 'Explore every angle.'; }
    }
    async load(auto = false) {
      if (!this.alive || !this.active || this.loading || this.ready || this.closest('[hidden]')) return;
      const token = ++this.requestToken, key = this.key;
      this.loading = true; this.dataset.state = 'loading';
      if (!auto) this.everAsked = true;
      this.loadButton.hidden = true; this.loadButton.disabled = true; this.spinner.hidden = false;
      // A load nobody asked for is not announced to screen readers; the caption still changes for those who can see it.
      this.speak(!auto);
      this.status.textContent = 'Loading the 3D view…'; this.setControls(false);
      this.controller = new AbortController(); let timedOut = false;
      // A load nobody asked for is given longer before it gives up quietly.
      this.loadTimeout = setTimeout(() => { timedOut = true; this.controller?.abort(); },auto ? 60000 : 30000);
      try {
        // The model starts downloading alongside the renderer script instead of after it.
        const model = this.config.models[key], sources = sourcesFor(model), stale = () => !this.alive || token !== this.requestToken || !this.active;
        const bytes = modelBytes(sources[0]); bytes.catch(() => {});
        const api = await abortable(loadRenderer(this.dataset.renderer),this.controller.signal);
        if (stale()) return;
        this.renderer = api.create(this.canvas);
        for (const [index,url] of sources.entries()) {
          try {
            const buffer = await abortable(index ? modelBytes(url) : bytes,this.controller.signal);
            if (stale()) return;
            await this.renderer.load(buffer,model.kind);
            break;
          } catch (error) {
            // If the lighter copy cannot be used, the original file is tried once; anything else is a real failure.
            if (index === sources.length - 1 || stale() || this.controller?.signal.aborted || /WebGL|shader/.test(error.message)) throw error;
            gone.add(url);
          }
        }
        if (stale()) return;
        this.ready = true; this.loading = false; this.dataset.state = 'ready'; this.everAsked = true;
        this.applyVariant();
        this.canvas.hidden = false; this.poster.hidden = true; this.spinner.hidden = true; this.loadButton.hidden = true;
        if (!this.userPaused && this.dataset.autoplay === 'true' && this.motionAllowed()) this.auto = true;
        if (this.auto) this.clearView();
        this.hint(); this.refresh();
        this.dispatchEvent(new CustomEvent('xe:3d-ready',{bubbles:true}));
      } catch (error) {
        if (!this.alive || token !== this.requestToken) return;
        this.renderer?.dispose(); this.renderer = null; this.loading = false; this.ready = false;
        const unsupported = /WebGL|shader/.test(error.message);
        this.canvas.hidden = true; this.poster.hidden = false; this.spinner.hidden = true;
        this.loadButton.hidden = false; this.loadButton.disabled = false; this.setControls(false);
        if (auto) {
          // Nobody asked for this load, so nothing is announced: the picture stays, the button returns, and the
          // gallery (if there is one) goes back to the photos.
          this.autoFailed = true; this.dataset.state = 'idle';
          this.loadButton.querySelector('span').textContent = 'EXPLORE IN 3D'; this.status.textContent = 'Explore every angle.';
          this.dispatchEvent(new CustomEvent('xe:3d-unavailable',{bubbles:true,detail:{unsupported}}));
        } else {
          this.dataset.state = 'error';
          this.loadButton.querySelector('span').textContent = 'TRY 3D AGAIN';
          this.status.textContent = unsupported ? '3D is unavailable in this browser. Explore the product photos instead.' : timedOut ? 'The 3D view took too long. Try again, or explore the photos.' : 'The 3D view couldn’t load. Try again, or explore the photos.';
        }
      } finally {
        if (token === this.requestToken) { clearTimeout(this.loadTimeout); this.controller = null; }
      }
    }
    refresh() {
      if (!this.alive) return;
      cancelAnimationFrame(this.frame); this.frame = 0; this.lastFrame = 0;
      if (!this.motionAllowed()) this.auto = false;
      this.setControls(this.ready);
      if (!this.canPaint()) { this.points.clear(); this.pinch = 0; this.disengage(); return; }
      if (!this.ready && !this.loading && this.dataset.state === 'idle') {
        // No model yet: load it, or (data saver, a slow link, an earlier failure) make sure the button is there to ask with.
        if (this.everAsked) this.load(); else if (this.wantsAuto()) this.load(true); else this.offerLoad();
      }
      // With an automatic load on its way there is no button to press.
      this.dataset.auto = String(this.wantsAuto());
      if (this.ready) this.requestPaint();
    }
    hint() {
      if (!this.ready) return;
      this.status.textContent = this.auto ? 'Rotating automatically. Drag to take control.' : fine.matches ? 'Drag to rotate. Click the model, then scroll to zoom.' : this.freeRotation ? 'Drag in any direction. Pinch to zoom. Turn the hand control off to scroll.' : 'Drag to turn the model in any direction. Pinch to zoom.';
    }
    // The caption under the model is a live region. It speaks for what the visitor does, not for what loads by itself.
    speak(on) { this.status.setAttribute('aria-live',on ? 'polite' : 'off'); }
    stopAuto() {
      this.auto = false; this.userPaused = true; this.tween = null; this.speak(true);
      this.setControls(this.ready); this.hint();
    }
    requestPaint() {
      this.dirty = true;
      if (!this.ready || !this.canPaint() || this.frame) return;
      this.frame = requestAnimationFrame(time => this.tick(time));
    }
    tick(time) {
      this.frame = 0;
      if (!this.ready || !this.canPaint()) return;
      const moving = this.auto && this.motionAllowed() || this.tween || this.spin;
      if (this.lastRender && time-this.lastRender < 32 && moving) { this.frame = requestAnimationFrame(t => this.tick(t)); return; }
      const dt = Math.min(this.lastFrame ? (time-this.lastFrame)/1000 : .016,.05); this.lastFrame = time;
      if (this.auto && this.motionAllowed()) { this.pose.angle += dt*.14; this.dirty = true; }
      if (this.spin) {
        // A flick carries the model on in the same direction and slows it to a stop.
        this.pose.angle += this.spin.a*dt; this.pose.elevation = Math.max(-1.4,Math.min(1.48,this.pose.elevation+this.spin.e*dt));
        const decay = Math.exp(-dt*3.5); this.spin.a *= decay; this.spin.e *= decay;
        if (Math.hypot(this.spin.a,this.spin.e) < .05) this.spin = null;
        this.dirty = true;
      }
      if (this.tween) {
        const progress = Math.min(1,Math.max(0,(time-this.tween.start)/420)), eased = 1-Math.pow(1-progress,3);
        for (const name of ['angle','elevation','zoom']) this.pose[name] = this.tween.from[name]+(this.tween.to[name]-this.tween.from[name])*eased;
        if (progress === 1) this.tween = null;
        this.dirty = true;
      }
      if (this.dirty) { this.renderer?.render(this.pose); this.dirty = false; this.lastRender = time; }
      if (this.auto && this.motionAllowed() || this.tween || this.spin) this.frame = requestAnimationFrame(t => this.tick(t));
    }
    view(name) {
      if (!this.ready) return;
      this.stopAuto(); this.spin = null;
      const kind = this.config.models[this.key].kind;
      const target = (cameraViews[kind] || {hero:[-.96,.43],front:[-Math.PI/2,.08],top:[-.96,1.48],under:[-.96,-.88]})[name];
      if (!target) return;
      const delta = Math.atan2(Math.sin(target[0]-this.pose.angle),Math.cos(target[0]-this.pose.angle));
      const to = {angle:this.pose.angle+delta,elevation:target[1],zoom:1};
      this.querySelectorAll('[data-xe-3d-view]').forEach(b => b.setAttribute('aria-pressed',String(b.getAttribute('data-xe-3d-view')===name)));
      if (this.angleSelect) this.angleSelect.value = name;
      if (this.motionAllowed()) this.tween = {from:{...this.pose},to,start:performance.now()}; else this.pose = to;
      this.requestPaint();
    }
    clearView() {
      this.querySelectorAll('[data-xe-3d-view]').forEach(b => b.setAttribute('aria-pressed','false'));
      if (this.angleSelect) this.angleSelect.value = '';
    }
    zoomBy(factor) {
      if (!this.ready) return;
      this.stopAuto(); this.pose.zoom = Math.max(.5,Math.min(2.4,this.pose.zoom*factor)); this.requestPaint();
    }
    // Touch (2.8.8): a drag turns the model every way, up and down as well as around. To keep the page scrollable, the
    // model only claims a finger that starts moving sideways (touch-action: pan-y); one that starts upwards scrolls the
    // page. Once the visitor has handled the model (a drag or a tap), it claims every direction for the next 3 seconds
    // (data-engaged, touch-action: none), so a drag straight up or down tilts it; after that the page scrolls again.
    pointerDown(event) {
      if (!this.ready || !this.canPaint() || event.button > 0) return;
      // A finger may be starting a page scroll, so a touch only stops the turning once it moves the model (pointerMove).
      if (event.pointerType !== 'touch') this.stopAuto();
      this.spin = null; this.velocity = {a:0,e:0,t:event.timeStamp};
      this.points.set(event.pointerId,{x:event.clientX,y:event.clientY,from:event.clientX,fromY:event.clientY,touch:event.pointerType === 'touch',moved:false});
      this.canvas.setPointerCapture?.(event.pointerId);
      if (event.pointerType === 'mouse') this.canvas.focus({preventScroll:true});
      if (this.points.size === 2) {
        const [a,b] = [...this.points.values()]; this.pinch = Math.hypot(a.x-b.x,a.y-b.y); this.engage();
      }
    }
    pointerMove(event) {
      const point = this.points.get(event.pointerId);
      if (!point || !this.ready || !this.canPaint()) return;
      const dx = event.clientX-point.x, dy = event.clientY-point.y; point.x=event.clientX; point.y=event.clientY;
      // A finger that has hardly moved may still be the start of a page scroll: the model waits for it to commit. While
      // the page owns upright swipes, one that starts more up than across is the page's (the browser scrolls it).
      if (!point.moved) {
        if (point.page) return;
        if (point.touch && this.points.size === 1) {
          const ox = event.clientX-point.from, oy = event.clientY-point.fromY;
          if (Math.hypot(ox,oy) < 8) return;
          if (this.dataset.engaged !== 'true' && !this.freeRotation && Math.abs(oy) > Math.abs(ox)) { point.page = true; return; }
        }
        point.moved = true; if (point.touch) this.engage();
      }
      if (this.auto && (this.points.size === 2 || Math.abs(event.clientX-point.from) > 6 || point.touch)) this.stopAuto();
      if (this.points.size === 2) {
        const [a,b] = [...this.points.values()], distance = Math.hypot(a.x-b.x,a.y-b.y);
        if (distance > 3 && this.pinch > 3) this.pose.zoom = Math.max(.5,Math.min(2.4,this.pose.zoom*this.pinch/distance));
        this.pinch = distance;
      } else if (this.points.size === 1) {
        const da = -dx*.009, de = dy*.007;
        this.pose.angle += da; this.pose.elevation = Math.max(-1.4,Math.min(1.48,this.pose.elevation+de));
        // how fast the model is being turned, for the flick when the finger lets go (radians per second, smoothed)
        const seconds = (event.timeStamp-this.velocity.t)/1000;
        if (seconds > 0) { const w = Math.min(1,seconds/.05); this.velocity.a += (da/seconds-this.velocity.a)*w; this.velocity.e += (de/seconds-this.velocity.e)*w; }
        this.velocity.t = event.timeStamp;
      }
      this.clearView(); this.requestPaint();
    }
    pointerUp(event) {
      const point = this.points.get(event.pointerId);
      if (!point) return;
      this.points.delete(event.pointerId); this.pinch = 0;
      const cancelled = event.type === 'pointercancel';
      // a tap on the model also means the visitor is handling it
      if (point.touch && !point.moved && !point.page && !cancelled && this.ready) this.engage();
      if (!this.points.size) {
        if (this.dataset.engaged === 'true') this.release();
        // a flick: let go while still moving
        const speed = Math.hypot(this.velocity.a,this.velocity.e), fresh = event.timeStamp-this.velocity.t < 80;
        if (point.moved && !cancelled && fresh && speed > .8 && !reduced.matches && this.ready) {
          const s = Math.min(1,8/speed); this.spin = {a:this.velocity.a*s,e:this.velocity.e*s}; this.requestPaint();
        }
      }
    }
    engage() {
      clearTimeout(this.engagedTimer); this.engagedTimer = 0;
      if (this.dataset.engaged !== 'true') this.dataset.engaged = 'true';
    }
    release() {
      clearTimeout(this.engagedTimer);
      this.engagedTimer = setTimeout(() => { this.engagedTimer = 0; this.dataset.engaged = 'false'; }, 3000);
    }
    disengage() {
      clearTimeout(this.engagedTimer); this.engagedTimer = 0; this.spin = null;
      if (this.dataset.engaged === 'true') this.dataset.engaged = 'false';
    }
    keyDown(event) {
      if (!this.ready || !this.canPaint() || !['ArrowLeft','ArrowRight','ArrowUp','ArrowDown','+','=','-','0'].includes(event.key)) return;
      event.preventDefault(); this.stopAuto();
      if (event.key==='0') { this.view('hero'); return; }
      if (event.key==='+' || event.key==='=') { this.zoomBy(.9); return; }
      if (event.key==='-') { this.zoomBy(1.1); return; }
      if (event.key==='ArrowLeft') this.pose.angle += .12;
      if (event.key==='ArrowRight') this.pose.angle -= .12;
      if (event.key==='ArrowUp') this.pose.elevation = Math.min(1.48,this.pose.elevation+.1);
      if (event.key==='ArrowDown') this.pose.elevation = Math.max(-1.4,this.pose.elevation-.1);
      this.clearView(); this.requestPaint();
    }
  }

  class ProductMedia extends HTMLElement {
    static get observedAttributes() { return ['data-xe-model-key']; }
    connectedCallback() {
      if (this.initialized) return;
      this.cleanup = []; this.viewer = this.querySelector('xe-product-viewer');
      this.tabs = this.querySelector('[data-xe-media-tabs]'); this.photos = this.querySelector('[data-xe-photo-panel]');
      this.threeD = this.querySelector('[data-xe-3d-panel]');
      if (!this.viewer || !this.tabs || !this.threeD) return;
      // Markup that arrives after this script has loaded (a section redrawn in the theme editor) upgrades a parent
      // before its children: make sure the viewer has read its settings before they are asked for.
      if (!this.viewer.config) { customElements.upgrade?.(this.viewer); if (!this.viewer.alive) this.viewer.connectedCallback?.(); }
      this.initialized = true; this.selectedView = 'photos';
      this.buttons = [...this.tabs.querySelectorAll('[data-xe-media-view]')]; this.tabs.setAttribute('role','tablist');
      for (const [index,button] of this.buttons.entries()) {
        button.setAttribute('role','tab');
        const panel = button.dataset.xeMediaView==='photos' ? this.photos : this.threeD;
        panel.setAttribute('role','tabpanel'); panel.setAttribute('aria-labelledby',button.id);
        listen(button,'click',() => this.select(button.dataset.xeMediaView,true),this.cleanup);
        listen(button,'keydown',event => {
          const target = event.key==='Home' ? 0 : event.key==='End' ? 1 : event.key==='ArrowRight' || event.key==='ArrowLeft' ? 1-index : -1;
          if (target < 0 || this.buttons[target].hidden) return;
          event.preventDefault(); this.select(this.buttons[target].dataset.xeMediaView,true); this.buttons[target].focus();
        },this.cleanup);
      }
      // Start fetching as soon as the visitor reaches for the 3D tab, before the click lands.
      for (const type of ['pointerenter','pointerdown','focus']) listen(this.buttons[1],type,() => { if (this.hasModel) warm(this.viewer,true); },this.cleanup,{passive:true});
      // A load that was never asked for and could not finish: show the photos again rather than a stalled picture.
      listen(this.viewer,'xe:3d-unavailable',event => {
        if (event.detail?.unsupported) { this.unsupported = true; this.syncModel(); }
        // Where the tabs are not shown (the home studio on phones) the viewer keeps its place and offers its button instead.
        else if (this.selectedView === '3d' && this.tabs.offsetParent !== null) this.select('photos',false);
      },this.cleanup);
      // Once one model is on screen, quietly fetch the models behind the section's other product tabs.
      listen(this.viewer,'xe:3d-ready',() => {
        const section = this.closest('[data-xe-spotlight]');
        section?.querySelectorAll('xe-product-viewer').forEach(other => { if (other !== this.viewer && other.config) warm(other,true); });
      },this.cleanup);
      this.syncModel();
      const first = this.firstView();
      // A gallery that opens in 3D on its own turns slowly, so it reads as a model and not as another photo.
      if (first === '3d' && this.dataset.defaultView !== '3d') this.viewer.dataset.autoplay = 'true';
      this.select(first,false);
    }
    // Which view opens first. "phone" and "all" come from the theme setting for product pages.
    firstView() {
      if (!this.hasModel) return 'photos';
      if (this.dataset.defaultView === '3d') return '3d';
      const mode = this.dataset.phoneView;
      return (mode === 'all' || mode === 'phone' && narrow.matches) && autoAllowed() && window.WebGLRenderingContext ? '3d' : 'photos';
    }
    attributeChangedCallback() { if (this.initialized) this.syncModel(); }
    disconnectedCallback() { this.destroy(); }
    destroy() {
      this.cleanup?.forEach(remove => remove()); this.cleanup = []; this.initialized = false;
      cancelAnimationFrame(this.layoutFrame); this.viewer?.destroy();
    }
    syncModel() {
      const key = this.dataset.xeModelKey;
      // The quick add window is for choosing options: it shows the photos only, as it always has.
      this.hasModel = Boolean(this.viewer.config?.models[key]) && !this.unsupported && !this.closest('quick-add-modal');
      this.tabs.hidden = !this.hasModel; this.buttons[1].hidden = !this.hasModel;
      if (this.hasModel) this.viewer.setModel(key);
      else this.select('photos',false);
    }
    select(view, start = false) {
      if (!this.initialized || view==='3d' && !this.hasModel) return;
      this.selectedView = view; this.dataset.view = view;
      this.photos.hidden = view!=='photos'; this.threeD.hidden = view!=='3d';
      this.buttons.forEach(button => { const selected=button.dataset.xeMediaView===view; button.setAttribute('aria-selected',String(selected)); button.tabIndex=selected?0:-1; });
      if (view==='3d') this.photos.querySelectorAll('video,model-viewer').forEach(media => media.pause?.());
      this.viewer.setActive(view==='3d',start);
      this.dispatchEvent(new CustomEvent('xe:media-view',{bubbles:true,detail:{view}}));
      cancelAnimationFrame(this.layoutFrame);
      if (view==='photos') this.layoutFrame = requestAnimationFrame(() => {
        this.photos.querySelectorAll('slider-component').forEach(slider => slider.resetPages?.());
      });
    }
  }
  if (!customElements.get('xe-product-viewer')) customElements.define('xe-product-viewer',ProductViewer);
  if (!customElements.get('xe-product-media')) customElements.define('xe-product-media',ProductMedia);
  // Shopify can unload a section before its DOM is removed.
  document.addEventListener('shopify:section:unload',event => {
    event.target.querySelectorAll('xe-product-media').forEach(media => media.destroy());
    event.target.querySelectorAll('xe-product-viewer').forEach(viewer => viewer.destroy());
  });
  document.addEventListener('shopify:section:load',event => {
    event.target.querySelectorAll('xe-product-viewer').forEach(viewer => viewer.connectedCallback());
    event.target.querySelectorAll('xe-product-media').forEach(media => media.connectedCallback());
  });
})();
